import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { WHATSAPP_LINK } from '../utils/constants';
import { ArrowRight, Truck, Shield, Clock } from 'lucide-react';

const Particle = ({ x, y, size, delay, duration }) => (
  <motion.div
    className="absolute rounded-full bg-brand-orange pointer-events-none"
    style={{ left: `${x}%`, top: `${y}%`, width: size, height: size, opacity: 0 }}
    animate={{ y: [0, -30, 0], opacity: [0, 0.4, 0] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
  />
);

const particles = [
  { x: 10, y: 20, size: 4, delay: 0,   duration: 4 },
  { x: 85, y: 15, size: 3, delay: 0.5, duration: 5 },
  { x: 20, y: 70, size: 5, delay: 1,   duration: 6 },
  { x: 75, y: 65, size: 3, delay: 1.5, duration: 4.5 },
  { x: 50, y: 85, size: 4, delay: 0.8, duration: 5.5 },
  { x: 92, y: 45, size: 3, delay: 2,   duration: 4 },
  { x: 5,  y: 50, size: 4, delay: 1.2, duration: 5 },
  { x: 60, y: 10, size: 3, delay: 0.3, duration: 6 },
];

const MagneticBtn = ({ children, className, href, onClick, target, rel }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15 });
  const sy = useSpring(y, { stiffness: 200, damping: 15 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.25);
    y.set((e.clientY - cy) * 0.25);
  };
  const handleLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.a
      ref={ref}
      href={href}
      onClick={onClick}
      target={target}
      rel={rel}
      style={{ x: sx, y: sy }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileTap={{ scale: 0.95 }}
      className={className}
    >
      {children}
    </motion.a>
  );
};

const Hero = () => (
  <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
    {/* Background gradient */}
    <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-brand-bg" />

    {/* Floating particles */}
    {particles.map((p, i) => <Particle key={i} {...p} />)}

    {/* Animated route lines */}
    <div className="absolute inset-0 z-0 pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute bg-brand-orange"
          style={{
            top: `${15 + i * 14}%`,
            left: '-100%',
            width: '100%',
            height: i % 2 === 0 ? '1px' : '0.5px',
            opacity: i % 2 === 0 ? 0.15 : 0.08,
            boxShadow: '0 0 8px #FF6B00',
          }}
          animate={{ left: '200%' }}
          transition={{ duration: 2.5 + i * 0.6, repeat: Infinity, ease: 'linear', delay: i * 0.4 }}
        />
      ))}
    </div>

    {/* Rotating ring decorations */}
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] sm:w-[550px] sm:h-[550px] md:w-[700px] md:h-[700px] border border-brand-orange/5 rounded-full pointer-events-none"
    />
    <motion.div
      animate={{ rotate: -360 }}
      transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] md:w-[500px] md:h-[500px] border border-brand-orange/8 rounded-full pointer-events-none"
    />

    {/* Pulsing center glow */}
    <motion.div
      animate={{ scale: [1, 1.3, 1], opacity: [0.04, 0.08, 0.04] }}
      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] bg-brand-orange rounded-full blur-[100px] pointer-events-none"
    />

    {/* Main content */}
    <div className="relative z-10 text-center px-4 sm:px-6 pt-28 sm:pt-32 md:pt-36 lg:pt-40 pb-16 sm:pb-20 max-w-5xl mx-auto w-full">

      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-brand-orange/20 rounded-full px-4 sm:px-5 py-2 mb-5 sm:mb-6 shadow-sm"
      >
        <motion.div
          animate={{ scale: [1, 1.4, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-2 h-2 rounded-full bg-brand-orange shrink-0"
        />
        <span className="text-brand-orange font-black tracking-[0.2em] sm:tracking-[0.35em] text-[10px] sm:text-xs uppercase">Next Generation Logistics</span>
      </motion.div>

      {/* Headline */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <motion.h1
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-1 sm:mb-2 leading-[1.05] tracking-tighter text-brand-black"
        >
          SMART ROUTES.
        </motion.h1>
        <motion.h1
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tighter text-brand-black"
        >
          <span className="text-brand-orange text-glow">STRONGER</span> DELIVERIES.
        </motion.h1>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="text-slate-900 max-w-2xl mx-auto mb-6 sm:mb-8 md:mb-10 text-sm sm:text-base md:text-lg lg:text-xl font-medium leading-relaxed px-2"
      >
        Experience the future of transport with PkRouteX. Cinematic efficiency, premium fleet, and unmatched reliability across the nation.
      </motion.p>

      {/* CTA Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 w-full"
      >
        <MagneticBtn
          href="#book"
          onClick={(e) => { e.preventDefault(); document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="group relative w-full sm:w-auto px-7 sm:px-8 py-3.5 sm:py-4 bg-brand-orange text-white font-black rounded-full overflow-hidden transition-all orange-glow flex items-center justify-center gap-2 text-xs sm:text-sm tracking-widest uppercase"
        >
          <span className="relative z-10 flex items-center gap-2">
            BOOK TRANSPORT
            <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
              <ArrowRight size={16} />
            </motion.span>
          </span>
          <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-600 ease-in-out" />
        </MagneticBtn>

        <MagneticBtn
          href={WHATSAPP_LINK("I'm interested in PkRouteX Logistics services")}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto px-7 sm:px-8 py-3.5 sm:py-4 border-2 border-brand-orange text-brand-orange font-black rounded-full hover:bg-brand-orange hover:text-white transition-all text-xs sm:text-sm tracking-widest uppercase text-center"
        >
          WHATSAPP US
        </MagneticBtn>
      </motion.div>

      {/* Trust badges */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5 }}
        className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-6"
      >
        {[
          { icon: Truck,  text: '50,000+ Deliveries' },
          { icon: Shield, text: '100% Insured' },
          { icon: Clock,  text: '24/7 Support' },
        ].map(({ icon: Icon, text }, i) => (
          <motion.div
            key={i}
            whileHover={{ y: -3, scale: 1.05 }}
            className="flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-black/8 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 shadow-sm"
          >
            <Icon size={13} className="text-brand-orange shrink-0" />
            <span className="text-brand-black text-[10px] sm:text-xs font-bold whitespace-nowrap">{text}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2 }}
      className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
    >
      <span className="text-[9px] font-black tracking-[0.3em] uppercase text-brand-orange/60">Scroll</span>
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        className="w-[1px] h-8 sm:h-10 bg-gradient-to-b from-brand-orange to-transparent"
      />
    </motion.div>
  </section>
);

export default Hero;
